// Apolo IDE — LearningPanel · Status sub-tab (Tier-S LRN5-T02).
//
// The audit's answer to "is the learning loop actually wired?" — every value is
// read live from GET /api/learning/status. Surfaces the loop/kill-switch flags
// (with an instant kill-switch toggle), the restored crons + freshness proxy,
// injected-tokens-per-turn telemetry, and the anti-self-delusion gate params.

import { useState } from 'react';
import { ShieldAlert, ShieldCheck, Clock, Gauge, Activity, Sparkles, Filter, HeartPulse, Siren, AlertTriangle, Minus } from 'lucide-react';
import {
  useLearningStatus,
  setKillSwitch,
  useBreakerStatus,
  useBreakerEvents,
  clearBreaker,
} from '../../../../hooks/useLearning.js';

function pct(x) {
  return `${Math.round((Number(x) || 0) * 100)}%`;
}

// liveness-feed E2 — feed-diagnostic chip. Each verdict carries a DISTINCT icon AND
// label (never color-only — Pedro runs macOS Increase Contrast, where color-only chips
// read as errors). `feed_suspect` is the only amber one; producing/nothing_to_mine are
// neutral. Observability-only: this chip never arms anything (L-B).
const FEED_CHIP = {
  producing: { label: 'Feed: produzindo', Icon: Activity, cls: 'bg-apolo-surface2 text-apolo-textSecondary' },
  nothing_to_mine: { label: 'Feed: sem material', Icon: Minus, cls: 'bg-apolo-surface2 text-apolo-textTertiary' },
  feed_suspect: { label: 'Feed: suspeito', Icon: AlertTriangle, cls: 'bg-apolo-warning/15 text-apolo-warning' },
};

function FeedChip({ feed }) {
  if (!feed || !feed.verdict) return null;
  const meta = FEED_CHIP[feed.verdict] || FEED_CHIP.nothing_to_mine;
  const Icon = meta.Icon;
  return (
    <span
      data-testid="feed-chip"
      role="status"
      title={feed.reason || ''}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}
    >
      <Icon size={11} aria-hidden="true" /> {meta.label}
    </span>
  );
}

function Row({ label, value, title }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs" title={title}>
      <span className="text-apolo-textTertiary">{label}</span>
      <span className="font-medium text-apolo-textPrimary">{value}</span>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return String(s); }
}

// liveness-feed E2 — compact relative time ("há 3h") for the breaker-events panel.
// Absolute time stays available as a title on the row for precision.
function timeAgo(s) {
  if (!s) return '—';
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return String(s);
  const diff = Date.now() - t;
  if (diff < 0) return 'agora';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}

export default function Status() {
  // 60s (was 15s): /api/learning/status runs the heartbeat aggregates over cron_runs
  // + outcomes; the loop's health changes slowly, so 15s was 4x the reads for no gain
  // (a contributor to the D1 read-limit blowup on 2026-09-03). Polling also pauses when
  // the tab is hidden (useLearning). Manual refetch stays instant.
  const { data, loading, error, refetch } = useLearningStatus({ pollIntervalMs: 60_000 });
  const breaker = useBreakerStatus({ pollIntervalMs: 30_000 });
  const breakerEvents = useBreakerEvents(10, { pollIntervalMs: 30_000 });
  const [busy, setBusy] = useState(false);
  const [clearMsg, setClearMsg] = useState(null);
  const status = data?.data || data; // apiFetch wraps in {ok,data}; tolerate both

  const flags = status?.flags || {};
  const killed = flags.kill_switch === true;

  async function toggleKill() {
    setBusy(true);
    try {
      await setKillSwitch(!killed, 'user');
      await refetch();
    } finally {
      setBusy(false);
    }
  }

  // E7 (LAW-6) — clear the GLOBAL breaker. The Worker dry-runs first and returns 409
  // { would_re_arm, reasons } if clearing would just re-arm next tick. We surface the
  // reasons and do NOT pretend it cleared.
  async function clearBreakerNow() {
    setBusy(true);
    setClearMsg(null);
    try {
      const res = await clearBreaker();
      if (res?.ok) {
        setClearMsg({ kind: 'ok', text: 'Breaker limpo.' });
        await Promise.all([refetch(), breaker.refetch?.(), breakerEvents.refetch?.()]);
      } else if (res?.status === 409) {
        const reasons = (res.data && res.data.reasons) || [];
        let text = `Bloqueado — armaria de novo: ${reasons.join(', ') || 'verdict ainda red'}`;
        // liveness-feed E2-WS2 — if the feed is suspect, tell the human WHY not to clear
        // blind: a broken feed means clearing won't fix the underlying starvation.
        const f = status?.feed;
        if (f && f.verdict === 'feed_suspect') {
          text += ` · feed suspeito: ${f.input_count ?? 0} sessões mineráveis, ${f.output_recent ?? 0} candidatos`;
        }
        setClearMsg({ kind: 'blocked', text });
      } else {
        setClearMsg({ kind: 'err', text: res?.error || 'Falha ao limpar o breaker' });
      }
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return <p role="alert" className="p-3 text-xs text-apolo-warning">Failed to load status: {error}</p>;
  }
  if (!status && loading) {
    return <p className="p-3 text-xs text-apolo-textTertiary">Loading learning status…</p>;
  }
  if (!status) {
    return <p className="p-3 text-xs text-apolo-textTertiary">No status available.</p>;
  }

  const inj = status.injected_tokens || {};
  const crons = status.crons || {};
  const gate = status.anti_self_delusion || {};
  const cov = status.coverage || {};
  // liveness-feed E1/E2 — the feed diagnostic (read-only): splits the governor's
  // `stale` into nothing_to_mine (healthy) vs feed_suspect (broken). Observability-only.
  const feed = status.feed || null;
  const hb = status.heartbeat_24h || null;
  // liveness-tier-s E1 — prefer the AUTHORITATIVE starvation verdict (BROKEN vs IDLE,
  // LAW-1) when present; fall back to the heartbeat's soft 24h proxy otherwise. A
  // healthy IDLE loop (empty_streak high, starved:false) must NOT paint the alarm.
  const sv = status.starvation || null;
  const hbStarved = sv
    ? sv.starved === true
    : !!(hb && hb.alarm && (hb.alarm.starved || (hb.alarm.zero_cycles ?? 0) >= 3));
  // Quiet (idle) cycles are observability ONLY — a neutral chip, never the red alarm.
  const quietCycles = sv ? Number(sv.empty_streak || 0) : 0;
  const showQuiet = !hbStarved && quietCycles >= 1;

  // E7 (LAW-6) — breaker recovery surface.
  const bs = breaker?.data?.data || breaker?.data || null;
  const wouldReArm = !!(bs && bs.would_be_now && bs.would_be_now.red);
  const wouldReasons = (bs && bs.would_be_now && bs.would_be_now.reasons) || [];
  const armedFlags = (bs && bs.armed_flags) || {};
  const breakerArmed = !!(armedFlags.kill_switch || armedFlags.promotion_paused);
  const events = (breakerEvents?.data?.data?.events || breakerEvents?.data?.events || []);

  return (
    <div className="flex flex-col gap-4 p-3">
      {/* autolearn E5 — self-improve is now PASSIVE/AUTOMATIC. Promotion runs itself, gated by the
          executable honest-green replay (never confidence) — you don't drive the menu anymore. */}
      <section aria-labelledby="ls-auto">
        <h3 id="ls-auto" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Sparkles size={12} aria-hidden="true" /> Auto-improvement
        </h3>
        <Row label="Mode" value="automatic" title="Apolo identifies + promotes its own improvements; the manual menu is observability only" />
        <Row label="Approver" value="replay-green" title="Promotion is gated by the executable honest-green replay (deterministic & matches & reachedGreen), never confidence" />
        <Row label="Running" value={crons.globally_killed ? 'no (killed)' : 'yes'} />
      </section>

      {/* liveness-governor E1 — 24h heartbeat: is the loop breathing? candidates in,
          outcomes closed, rules applied over the last 24h + a loud chip if starved.
          The chip is the read-only liveness sensor surfaced; the breaker (E4) governs. */}
      <section aria-labelledby="ls-heartbeat">
        <h3 id="ls-heartbeat" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <HeartPulse size={12} aria-hidden="true" /> Heartbeat (24h)
        </h3>
        {hb ? (
          <>
            <div className="mb-1.5">
              <span
                data-testid="hb-chip"
                role="status"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  hbStarved
                    ? 'bg-apolo-warning/15 text-apolo-warning'
                    : 'bg-apolo-surface2 text-apolo-textTertiary'
                }`}
              >
                {hbStarved ? `Starved · ${hb.alarm?.zero_cycles ?? 0} zero cycles` : 'Live'}
              </span>
              {showQuiet ? (
                <span
                  data-testid="quiet-chip"
                  className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-apolo-surface2 px-2 py-0.5 text-[11px] font-medium text-apolo-textTertiary"
                  title="O cron rodou e produziu nada porque não havia o que minerar (idle saudável) — não é falha, não arma o breaker."
                >
                  {quietCycles} {quietCycles === 1 ? 'ciclo quieto' : 'ciclos quietos'}
                </span>
              ) : null}
            </div>
            <Row label="Candidates in" value={hb.candidates_in ?? 0} title="learning_candidates created in the last 24h" />
            <Row label="Outcomes matched" value={hb.outcomes_matched ?? 0} title="agent_outcomes with a real verdict in the last 24h" />
            <Row label="Rules applied" value={hb.rules_applied ?? 0} title="candidates promoted/applied in the last 24h" />
            <Row label="Last candidate" value={fmtDate(hb.last_candidate_at)} />
          </>
        ) : (
          <p className="text-xs text-apolo-textTertiary">Heartbeat unavailable.</p>
        )}
      </section>

      {/* autolearn E5/E2 — the funnel: is the loop FED or starved? Distinguishes "ran and produced 0"
          from "never ran" so starvation is visible instead of silently averaging over an empty ledger. */}
      <section aria-labelledby="ls-funnel">
        <h3 id="ls-funnel" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Filter size={12} aria-hidden="true" /> Ledger funnel
        </h3>
        {feed ? (
          <>
            <div className="mb-1.5">
              <FeedChip feed={feed} />
            </div>
            <Row label="Sessões mineráveis (24h)" value={feed.input_count ?? 0} title="DISTINCT sessions com user_message na janela — o input que o patternDetector consome" />
            <Row label="Candidatos (24h)" value={feed.output_recent ?? 0} title="learning_candidates criados na janela — o output do loop" />
            <Row label="Último candidato" value={fmtDate(feed.last_candidate_at)} />
          </>
        ) : null}
        <Row label="Runs recorded" value={cov.total ?? 0} title="agent_outcomes rows — the run-grain ledger" />
        <Row label="Verified (honest-green)" value={`${cov.verified ?? 0} · ${pct(cov.green_pass_coverage)}`} title="runs with a real verifier verdict (green_pass not null)" />
        <Row label="Externally confirmed" value={`${cov.ext_confirmed ?? 0} · ${pct(cov.ext_signal_coverage)}`} title="runs a human merged (the anti-self-delusion signal)" />
      </section>

      {/* Flags + kill-switch */}
      <section aria-labelledby="ls-flags">
        <h3 id="ls-flags" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          {killed ? <ShieldAlert size={12} aria-hidden="true" /> : <ShieldCheck size={12} aria-hidden="true" />}
          Loop state
        </h3>
        <Row label="Learning loop" value={flags.loop_enabled ? 'ON' : 'OFF'} />
        <Row label="Kill-switch (you)" value={killed ? 'ARMED' : 'off'} />
        <Row label="Global kill" value={flags.global_kill ? 'ARMED' : 'off'} title="env LEARNING_KILL_SWITCH or the global D1 flag" />
        <button
          type="button"
          onClick={toggleKill}
          disabled={busy}
          aria-pressed={killed}
          className={`mt-2 w-full rounded-md px-2 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50 ${
            killed
              ? 'bg-apolo-surface2 text-apolo-textPrimary hover:bg-apolo-surface3'
              : 'bg-apolo-warning/15 text-apolo-warning hover:bg-apolo-warning/25'
          }`}
        >
          {busy ? 'Applying…' : killed ? 'Disarm kill-switch (resume learning)' : 'Arm kill-switch (stop all learning)'}
        </button>
        <p role="status" className="sr-only">
          {killed ? 'Learning kill-switch armed; injection and crons are off.' : 'Learning loop active.'}
        </p>
      </section>

      {/* liveness-tier-s E7 — breaker recovery: would-re-arm chip + last events + a
          gated Clear (the Worker blocks a clear that would just re-arm next tick). */}
      <section aria-labelledby="ls-breaker">
        <h3 id="ls-breaker" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Siren size={12} aria-hidden="true" /> Breaker (auto-governança)
        </h3>
        {bs ? (
          <>
            <div className="mb-1.5">
              <span
                data-testid="rearm-chip"
                role="status"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  wouldReArm ? 'bg-apolo-warning/15 text-apolo-warning' : 'bg-apolo-surface2 text-apolo-textTertiary'
                }`}
                title={wouldReArm ? wouldReasons.join(', ') : 'O verdict atual está verde — um Clear pegaria.'}
              >
                {wouldReArm ? `Armaria de novo: ${wouldReasons.join(', ') || 'verdict red'}` : 'Não armaria agora'}
              </span>
            </div>
            <Row label="Kill-switch (breaker)" value={armedFlags.kill_switch ? 'ARMED' : 'off'} />
            <Row label="Promotion paused" value={armedFlags.promotion_paused ? 'ARMED' : 'off'} />
            <button
              type="button"
              onClick={clearBreakerNow}
              disabled={busy || !breakerArmed}
              className="mt-2 w-full rounded-md bg-apolo-surface2 px-2 py-1.5 text-xs font-medium text-apolo-textPrimary transition hover:bg-apolo-surface3 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
            >
              {busy ? 'Aplicando…' : 'Clear breaker (global)'}
            </button>
            {clearMsg ? (
              <p
                role="status"
                data-testid="clear-msg"
                className={`mt-1.5 text-[11px] ${clearMsg.kind === 'blocked' || clearMsg.kind === 'err' ? 'text-apolo-warning' : 'text-apolo-textTertiary'}`}
              >
                {clearMsg.text}
              </p>
            ) : null}
            {/* liveness-feed E2 — hardened: explicit loading / error / empty states so the
                panel never silently blanks, + relative time (absolute kept as title). */}
            <div className="mt-2">
              <p className="mb-1 text-[11px] font-medium text-apolo-textTertiary">Últimos breaker events</p>
              {breakerEvents.error ? (
                <p data-testid="be-error" className="text-[11px] text-apolo-textTertiary">Não foi possível carregar os eventos.</p>
              ) : breakerEvents.loading && events.length === 0 ? (
                <p data-testid="be-loading" className="text-[11px] text-apolo-textTertiary">Carregando eventos…</p>
              ) : events.length === 0 ? (
                <p data-testid="be-empty" className="text-[11px] text-apolo-textTertiary">Nenhum evento de breaker.</p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {events.slice(0, 10).map((e) => (
                    <li key={e.id} className="flex items-center justify-between gap-2 text-[11px] text-apolo-textTertiary" title={`${(e.reasons || []).join(', ')}${e.reasons?.length ? ' · ' : ''}${fmtDate(e.fired_at)}`}>
                      <span className="font-medium text-apolo-textSecondary">{e.action}</span>
                      <span className="tabular-nums" title={fmtDate(e.fired_at)}>{timeAgo(e.fired_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <p className="text-xs text-apolo-textTertiary">Breaker status indisponível.</p>
        )}
      </section>

      {/* Crons */}
      <section aria-labelledby="ls-crons">
        <h3 id="ls-crons" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Clock size={12} aria-hidden="true" /> Crons
        </h3>
        {(crons.schedules || []).map((cr) => (
          <Row key={cr.schedule} label={cr.schedule} value={(cr.jobs || []).join(', ')} />
        ))}
        <Row label="Crons running" value={crons.globally_killed ? 'no (killed)' : 'yes'} />
        <Row label="Last candidate" value={fmtDate(crons.last_candidate_at)} />
      </section>

      {/* Injected tokens */}
      <section aria-labelledby="ls-inj">
        <h3 id="ls-inj" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Gauge size={12} aria-hidden="true" /> Injected tokens / turn
        </h3>
        <Row label="Avg" value={inj.avg_tokens ?? 0} />
        <Row label="Max" value={inj.max_tokens ?? 0} />
        <Row label="Samples" value={inj.samples ?? 0} />
      </section>

      {/* Anti-self-delusion gate */}
      <section aria-labelledby="ls-gate">
        <h3 id="ls-gate" className="mb-1 flex items-center gap-1 text-xs font-semibold text-apolo-textSecondary">
          <Activity size={12} aria-hidden="true" /> Anti-self-delusion gate
        </h3>
        <Row label="Active" value={gate.active ? 'yes' : 'no'} />
        <Row label="Min external coverage" value={gate.min_coverage ?? '—'} title="A learned rule needs human/external signal, never the agent's own test" />
      </section>
    </div>
  );
}
